<template lang="pug">
    .chart-container.shopsales-chart(v-if="enabled" v-loading="loading")
        h3.title {{ $t('Shop Sales') }} &nbsp;
        el-form.shop-filters.flex-row(size="mini" label-position="top")
            el-form-item(:label="$t('Currency')")
                el-select(
                    placeholder=""
                    filterable
                    v-model="selectedCurrency"
                    autocomplete="no-fill"
                    :disabled="currencyOptions.length <= 1"
                )
                    el-option(
                        v-for="c in currencyOptions"
                        :key="c.id"
                        :value="c.label"
                    )
            el-form-item(:label="$t('Customer Groups')")
                el-select.is-full-width(
                    v-model="selectedCustomerGroups"
                    placeholder=""
                    filterable
                    clearable
                    multiple
                    autocomplete="no-fill"
                    :disabled="customerGroupOptions.length <= 1"
                    @change="refetch"
                )
                    el-option(
                        v-for="cg in customerGroupOptions"
                        :key="cg.id"
                        :label="cg.name"
                        :value="cg.id"
                    )
        //- template(v-if="runningJobs.length")
        //-     .job
        //-         el-progress(
        //-             :percentage="Math.round(runningJobs[0].percentage * 100) / 100"
        //-             :width="50"
        //-             :show-text="false"
        //-         )
        //-         .summary(v-if="runningJobs[0].percentage && runningJobs[0].current > 0")
        //-             small {{ runningJobs[0].current }} / {{ runningJobs[0].total }}
        //-             small {{ runningJobs[0].percentage.toFixed(2) }}%

        //- transition(name="slide-decent-vertical")
        //-     .status-logs-container.z-depth-1(v-if="logs.length")
        //-         .spinner-container(v-if="loadingLogs")
        //-             .spinner
        //-         StatusLog(v-for="log in logs" :key="log.id" :log="log")
        //-             template(v-if="['success', 'error'].includes(log.status)" slot="actions")
        //-                 a.is-danger(href="#" @click.prevent="hideStatusLog(log)")
        //-                     i.el-icon-fa-times.fas

        .error(v-if="error")
            template(v-if="error.code")
                Error(:message="$t(error.message)" :code="error.code")
            template(v-else)
                Error(:message="$t('Something went wrong!')" :code="501")
        bar-chart.chart(
            v-else
            ref="chart"
            :chart-data="datacollection"
            :options="chartOptions"
            :height="400"
        )
        .legend(v-if="!(error && [401, 403].includes(error.code))")
            .row
                el-switch(
                    v-if="stackable"
                    :active-text="$t('Stacked')"
                    inactive-color="#ff4949"
                    :value="stacked"
                    @change="toggleStacked"
                )
                ul.no-style-list
                    li.all-sales
                        el-button(
                            size="mini"
                            @click="toggleDatasets(['all-sales'], $event)"
                        )
                            span.color(:style="getLegendStyle('all-sales')")
                            span.label(:class="{ 'strike-through': disabledDatasets.includes('all-sales') }")
                                template(v-if="enabledExtensions.includes('shop:point-of-sale')")
                                    template(v-if="campaigns.length && enabledExtensions.includes('marketing') && stacked")
                                        | {{ $t('Webshop Sales Volume Without Campaign') }}
                                    template(v-else)
                                        | {{ $t('Webshop Sales Volume') }}
                                template(v-else)
                                    template(v-if="campaigns.length && enabledExtensions.includes('marketing') && stacked")
                                        | {{ $t('Sales Volume Without Campaign') }}
                                    template(v-else)
                                        | {{ $t('Sales Volume') }}
                    template(v-if="pointsOfSale.length && enabledExtensions.includes('shop:point-of-sale')")
                        li.point-of-sale(v-for="pointOfSale in pointsOfSale")
                            el-button(
                                size="mini"
                                @click="toggleDatasets([pointOfSale.key], $event)"
                            )
                                span.color(:style="getLegendStyle(pointOfSale.key)")
                                span.label(:class="{ 'strike-through': disabledDatasets.includes(pointOfSale.key) }")
                                    | {{ pointOfSale.name }}
            template(v-if="(campaigns.length && enabledExtensions.includes('marketing')) || marketplaceCampaign || externalMarketplaces.length")
                hr
                ul.no-style-list
                    li.campaign(v-for="campaign in campaigns")
                        el-button(
                            size="mini"
                            @click="toggleDatasets([campaign.key], $event)"
                        )
                            span.color(:style="getLegendStyle(campaign.key)")
                            span.label(:class="{ 'strike-through': disabledDatasets.includes(campaign.key) }")
                                | {{ campaign.name }}
                    li.divider(v-if="campaigns.length && (marketplaceCampaign || externalMarketplaces.length)")
                    li.campaign(v-for="marketplace in externalMarketplaces")
                        el-button(
                            size="mini"
                            @click="toggleDatasets([marketplace.key], $event)"
                        )
                            span.color(:style="getLegendStyle(marketplace.key)")
                            span.label(:class="{ 'strike-through': disabledDatasets.includes(marketplace.key) }")
                                | {{ marketplace.name }}
                    li.divider(v-if="externalMarketplaces.length && marketplaceCampaign")
                    li.campaign(v-if="marketplaceCampaign")
                        el-button(
                            size="mini"
                            @click="toggleDatasets(['city-offer-marketplace'], $event)"
                        )
                            span.color(:style="getLegendStyle('city-offer-marketplace')")
                            span.label(:class="{ 'strike-through': disabledDatasets.includes('city-offer-marketplace') }")
                                | {{ $t('CityOffer Marketplace') }}
    .chart-container.disabled(v-else-if="!loading")
        h3.title {{ $t('Shop Sales') }}
        p.body {{ $t('Widget is disabled!') }}
</template>

<i18n>
{
    "en": {
        "Total: {total}": "Total: {total}"
    },
    "de": {
        "CityOffer Marketplace": "CityOffer Marktplatz",
        "Widget is disabled!": "Widget ist deaktiviert!",
        "Shop Sales": "Shopverkäufe",
        "Sales Volume": "Umsatz",
        "Sales Volume Without Campaign": "Umsatz ohne Kampagne",
        "Webshop Sales Volume": "Webshop Umsatz",
        "Webshop Sales Volume Without Campaign": "Webshop Umsatz ohne Kampagne",
        "Number Of Orders": "Anzahl der Bestellungen",
        "Total: {total}": "Gesamt: {total}",
        "Stacked": "Gestapelt",
        "Daterange": "Zeitraum",
        "Currency": "Währung",
        "Customer Groups": "Kundengruppen",
        "Period": "Intervall",
        "Hourly": "Stündlich",
        "Daily": "Täglich",
        "Weekly": "Wöchentlich",
        "Monthly": "Monatlich",
        "Yearly": "Jährlich",
        "Week": "Kalenderwoche",
        "The summary will be processed asynchronously": "Die Zusammenfassung wird asynchron verarbeitet",
        "You will be notified once the summary is completed": "Sie werden benachrichtigt, sobald die Zusammenfassung abgeschlossen ist"
    }
}
</i18n>

<script>
import Color from 'color';
import stc from 'string-to-color';

import StatisticsApi from '@/store/api/statistics';

import isPast from 'date-fns/isPast';
import compareAsc from 'date-fns/compareAsc';

import startOfHour from 'date-fns/startOfHour';
import endOfHour from 'date-fns/endOfHour';

import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';

import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';

import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';

import startOfYear from 'date-fns/startOfYear';
import endOfYear from 'date-fns/endOfYear';

import startOfDecade from 'date-fns/startOfDecade';
import endOfDecade from 'date-fns/endOfDecade';

import getUnixTime from 'date-fns/getUnixTime'
import fromUnixTime from 'date-fns/fromUnixTime'

import addHours from 'date-fns/addHours'
import subHours from 'date-fns/subHours'

import addDays from 'date-fns/addDays'
import subDays from 'date-fns/subDays'

import addWeeks from 'date-fns/addWeeks'
import subWeeks from 'date-fns/subWeeks'

import addMonths from 'date-fns/addMonths'
import subMonths from 'date-fns/subMonths'

import addYears from 'date-fns/addYears'
import subYears from 'date-fns/subYears'

import differenceInHours from 'date-fns/differenceInHours'
import differenceInDays from 'date-fns/differenceInDays'
import differenceInWeeks from 'date-fns/differenceInWeeks'
import differenceInMonths from 'date-fns/differenceInMonths'
import differenceInYears from 'date-fns/differenceInYears'

import { adminClient as apolloClient } from '@/store/apollo';
import { moneyFormat, formatDate } from '@/filters'
import { appNow, appGetUnixTime, appFromUnixTime } from '@/utils/timezone'

import SiteViewsChart from './SiteViewsChart'
import BarChart from '@/components/BarChart'

export default {
    name: 'ShopSalesChart',
    extends: SiteViewsChart,
    props: {
        domain: {
            type: String,
            required: false,
        },
    },
    data() {
        const defaultCurrency = _.get(this.activeCms, 'shop.defaultCustomerGroup.currency.iso4217') || null;
        const defaultCustomerGroups = defaultCurrency
            ? _.get(this.activeCms, 'shop.customerGroups', [])
                .filter(cg => cg.currency && cg.currency.iso4217 === defaultCurrency)
                .map(cg => cg.id)
            : [];
        const stacked = true;
        const component = this;
        return {
            selectedCurrency: defaultCurrency,
            selectedCustomerGroups: defaultCustomerGroups,
            stacked,
            loading: false,
            error: false,
            right: 'statistics:sales-summaries',
            windowWidth: undefined,
            datacollection: undefined,
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                animation: {
                    duration: 1000,
                },
                plugins: {
                    legend: {
                        position: 'bottom',
                        align: 'end'
                    },
                    tooltip: {
                        mode: stacked ? 'index' : 'nearest',
                        intersect: false,
                        callbacks: {
                            label: (context) => {
                                const label = context.dataset.label || '';
                                const value = context.parsed.y;
                                if (isNaN(value)) return null;

                                const money = moneyFormat(value, this.currency)
                                return `${label}: ${money}`;
                            },
                            footer: (tooltipItems) => {
                                if (stacked && tooltipItems.filter(el => el.parsed.y).length > 1) {
                                    let total = 0;
                                    tooltipItems.forEach(el => el.parsed.y ? total += el.parsed.y : null);
                                    return this.$t('Total: {total}', { total: moneyFormat(total, this.currency) })
                                }
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        stacked
                    },
                    y: {
                        stacked,
                        beginAtZero: true,
                        ticks: {
                            precision: 0,
                            callback: (value) => {
                                return moneyFormat(value, this.currency)
                            }
                        }
                    },
                },
                onHover: (event, elements) => {
                    if (elements.length && component.period !== 'hour') {
                        event.native.target.style.cursor = 'pointer';
                    } else {
                        event.native.target.style.cursor = 'default';
                    }
                },
                onClick: this.handleClick
            },

            // logKeys: ['summarize-sales-statistics'],
            // loadingLogs: false,
            // logs: [],

            rawData: [],
        }
    },
    components: {
        BarChart
    },
    watch: {
        activeCms: {
            immediate: true,
            handler(cms) {
                if (cms && !this.selectedCurrency) {
                    this.selectedCurrency = _.get(cms, 'shop.defaultCustomerGroup.currency.iso4217') || null;
                    this.selectedCustomerGroups = this.customerGroupOptions.map(cg => cg.id);
                }
            },
        },
        selectedCurrency() {
            // Reset customer groups when currency changes, then refetch
            this.$nextTick(() => {
                this.selectedCustomerGroups = this.customerGroupOptions.map(cg => cg.id);
                this.refetch();
            });
        },
    },
    computed: {
        currency() {
            return this.selectedCurrency || _.get(this.activeCms, 'shop.defaultCustomerGroup.currency.iso4217');
        },
        currencyOptions() {
            const options = [];
            const currencies = _.get(this.activeCms, 'shop.currencies') || [];
            currencies.forEach(el => {
                if (options.find(opt => opt.label === el.iso4217)) return;
                options.push({ id: el.id, label: el.iso4217 });
            });
            return options;
        },
        customerGroupOptions() {
            const options = [];
            const customerGroups = _.get(this.activeCms, 'shop.customerGroups') || [];
            customerGroups.forEach(el => {
                if (!el.currency || el.currency.iso4217 !== this.selectedCurrency) return;
                options.push(el);
            });
            return options;
        },
        enabled() {
            return this.enabledExtensions.includes('shop');
        },
        stackable() {
            return this.campaigns.length || this.pointsOfSale.length || this.externalMarketplaces.length || !!this.marketplaceCampaign;
        },
        externalMarketplaces() {
            const marketplaces = [];
            if (this.rawData && this.rawData.length) {
                this.rawData.forEach(element => {
                    if (!element.sold_by || element.status.key === 'canceled') return;
                    if (['web-shop', 'point-of-sale'].indexOf(element.sold_by) >= 0) return;
                    const key = 'marketplace:' + element.sold_by;
                    if (!marketplaces.find(marketplace => marketplace.key === key)) {
                        marketplaces.push({
                            key,
                            soldBy: element.sold_by,
                            name: element.sold_by,
                        });
                    }
                });
            }
            const cityOfferIndex = marketplaces.findIndex(marketplace => marketplace.soldBy === 'city-offer-marketplace' || marketplace.soldBy === 'city-offer');
            if (cityOfferIndex >= 0) {
                const cityOfferItem = marketplaces.splice(cityOfferIndex, 1)[0];
                marketplaces.push(cityOfferItem);
            }
            return marketplaces;
        },
        campaigns() {
            const campaigns = [];
            if (this.rawData && this.rawData.length) {
                this.rawData.forEach(element => {
                    if (!element.campaign_key || element.status.key === 'canceled') return;
                    if (element.campaign_key === this.marketplaceCampaignKey) return;
                    if (!campaigns.find(campaign => campaign.key === element.campaign_key)) {
                        campaigns.push({
                            key: element.campaign_key,
                            name: element.campaign_name,
                        });
                    }
                });
            }
            return campaigns;
        },
        pointsOfSale() {
            let pointsOfSale = [];
            if (this.rawData && this.rawData.length) {
                this.rawData.forEach(el => {
                    if (! el.pointOfSale || el.status.key === 'canceled') return;
                    if (! pointsOfSale.find(pos => pos.key === el.pointOfSale.key)) {
                        pointsOfSale.push({
                            key: el.pointOfSale.key,
                            name: el.pointOfSale.name,
                        });
                    }
                });
            }
            return pointsOfSale;
        },
    },
    methods: {
        async forceUpdate() {
            this.loading = true;

            await apolloClient.clearStore()
            StatisticsApi.generateSalesStatistics({ shop: this.activeCms.shop.id })
                .then(() => {
                    this.$notify.success({
                        title: this.$t('The summary will be processed asynchronously'),
                        message: this.$t('You will be notified once the summary is completed'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 3000
                    });
                })
                .finally(() => {
                    this.loading = false;
                })
        },
        refetch(loading = true) {
            if (!_.get(this.activeCms, 'shop.id')) return

            this.loading = loading;

            let filters = {
                shop: _.get(this.activeCms, 'shop.id') || null,
                currency: this.selectedCurrency,
                customerGroups: this.selectedCustomerGroups || [],
                period: this.period,
                from: appGetUnixTime(this.daterange[0]),
                to: appGetUnixTime(this.daterange[1]),
            };

            StatisticsApi.fetchSalesStatistics(filters)
                .then(data => {
                    this.error = false;

                    this.rawData = data;

                    this.$nextTick(() => {
                        this.generate();
                        if (this.$refs.chart) {
                            this.$refs.chart.renderChart(this.datacollection, this.chartOptions)
                            setTimeout(() => this.chartOptions.animation.duration = 0, 1000);
                        }
                    })
                })
                .catch(err => {
                    this.datacollection = undefined;

                    const { graphQLErrors } = err

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    switch (code) {
                        case 401:
                            this.error = { code, message: 'Unauthorized' };
                            break;
                        case 403:
                            this.error = { code, message: 'Not Allowed' };
                            break;
                        // case 404:
                        //     break;
                        default:
                            this.error = true;
                            break;
                    }

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                })
                .finally(() => {
                    this.loading = false
                })

        },
        generate() {
            let addMethod;
            let differenceMethod;
            let periodStartMethod;

            let chartStartMethod;
            let chartEndMethod;

            let format = this.$t('yyyy-MM-dd');

            let data = []
            let labels = []

            const from = this.daterange[0]
            const to = this.daterange[1]

            switch (this.period) {
                case 'hour':
                    differenceMethod = differenceInHours;
                    periodStartMethod = startOfHour;
                    chartStartMethod = startOfDay;
                    chartEndMethod = endOfDay;
                    addMethod = addHours;
                    format = 'HH:mm';
                    break;

                case 'week':
                    differenceMethod = differenceInWeeks;
                    periodStartMethod = date => startOfWeek(date, {weekStartsOn: 1});
                    chartStartMethod = startOfMonth;
                    chartEndMethod = endOfMonth;
                    addMethod = addWeeks;
                    format = `'${this.$t('Week')}' ww`;
                    break;

                case 'month':
                    differenceMethod = differenceInMonths;
                    periodStartMethod = startOfMonth;
                    chartStartMethod = startOfYear;
                    chartEndMethod = endOfYear;
                    addMethod = addMonths;
                    format = 'LLLL yyyy';
                    break;

                case 'year':
                    differenceMethod = differenceInYears;
                    periodStartMethod = startOfYear;
                    chartStartMethod = startOfDecade;
                    chartEndMethod = endOfDecade;
                    addMethod = addYears;
                    format = 'yyyy';
                    break;

                default:
                // case 'day':
                    differenceMethod = differenceInDays;
                    periodStartMethod = startOfDay;
                    chartStartMethod = date => startOfWeek(date, {weekStartsOn: 1});
                    chartEndMethod = date => endOfWeek(date, {weekStartsOn: 1});
                    addMethod = addDays;
                    break;
            }

            if (from && to) {
                let current = from;
                while (current <= to) {
                    const label = formatDate(current, format);
                    labels.push(label);

                    // if (current > new Date()) data.push(NaN);
                    // else data.push(0)

                    data.push(null)

                    current = addMethod(current, 1);
                }
            }

            let label = this.$t('Sales Volume');
            if (this.enabledExtensions.includes('shop:point-of-sale')) {
                label = this.$t('Webshop Sales Volume');
            }

            const hasCampaigns = this.campaigns.length || this.marketplaceCampaign;

            if (hasCampaigns) {
                if (this.enabledExtensions.includes('marketing') && this.stacked) {
                    label = this.$t('Sales Volume Without Campaign');
                    if (this.enabledExtensions.includes('shop:point-of-sale')) {
                        label = this.$t('Webshop Sales Volume Without Campaign');
                    }
                } else {
                    // allVisitorsLabel = this.$t('Unique Visitors'),
                }
            }

            this.datacollection = {
                labels,
                datasets: [
                    {
                        key: 'all-sales',
                        hidden: this.disabledDatasets.indexOf('all-sales') >= 0,
                        label,
                        // borderColor: 'rgb(149, 192, 31)',
                        backgroundColor: 'rgba(149, 192, 31, 0.3)',
                        borderWidth: 1.2,
                        fill: true,
                        data: _.cloneDeep(data)
                    },
                    // {
                    //     label: this.$t('Number Of Orders'),
                    //     borderColor: 'rgb(107, 182, 239)',
                    //     backgroundColor: 'rgba(107, 182, 239, 0.7)',
                    //     borderWidth: 1.2,
                    //     fill: true,
                    //     data: _.cloneDeep(data)
                    // }
                ],
            }

            if (this.marketplaceCampaign) {
                this.datacollection.datasets.push({
                    key: 'city-offer-marketplace',
                    hidden: this.disabledDatasets.indexOf('city-offer-marketplace') >= 0,
                    label: this.$t('CityOffer Marketplace'),
                    backgroundColor: Color('#be1521').fade(0.3).rgb().string(),
                    fill: false,
                    pointHitRadius: 30,
                    pointRadius: 3,
                    pointHoverRadius: 7,
                    data: _.cloneDeep(data)
                });
            }

            const now = differenceMethod(periodStartMethod(appNow()), chartStartMethod(appNow()))
            const chartIsInThePast = isPast(chartEndMethod(this.daterange[1]))

            const sales = this.rawData
            if (sales && sales.length) {
                _.cloneDeep(sales).sort((a, b) => a.start > b.start).forEach(el => {
                    if (el.status.key === 'canceled') return;

                    const i = differenceMethod(appFromUnixTime(el.start), periodStartMethod(from))

                    // if (this.enabledExtensions.includes('marketing') && this.stacked) {
                    //     if (! el.campaign_key) this.datacollection.datasets[0].data[i] += el.gross;
                    // } else {
                    //     this.datacollection.datasets[0].data[i] += el.gross;
                    // }
                    // this.datacollection.datasets[1].data[i] += el.purchases;

                    if (el.campaign_key && (el.campaign_key === 'city-offer-marketplace' || this.enabledExtensions.includes('marketing'))) {
                        const hex = _.get(el.campaign, 'color') || '#6b52ef';

                        const campaignDatasetIndex = this.datacollection.datasets.findIndex(dataset => {
                            return dataset.key === el.campaign_key;
                        });
                        if (campaignDatasetIndex >= 0) {
                            if (this.datacollection.datasets[campaignDatasetIndex].data[i] === undefined) {
                                this.datacollection.datasets[campaignDatasetIndex].data[i] = 0;
                            }
                            this.datacollection.datasets[campaignDatasetIndex].data[i] += el.gross;
                        } else {
                            const campaignDataset = {
                                key: el.campaign_key,
                                hidden: this.disabledDatasets.indexOf(el.campaign_key) >= 0,
                                label: el.campaign_name,
                                // borderColor: hex,
                                backgroundColor: Color(hex).fade(0.3).rgb().string(),
                                fill: false,
                                // borderDash: [10,10],
                                // lineTension: 0.4,
                                pointHitRadius: 30,
                                pointRadius: 3,
                                pointHoverRadius: 7,
                                data: _.cloneDeep(data)
                            };
                            campaignDataset.data[i] = el.gross;
                            this.datacollection.datasets.push(campaignDataset);
                        }
                    } else if (el.pointOfSale && el.pointOfSale.key && this.enabledExtensions.includes('shop:point-of-sale')) {
                        const hex = _.get(el.pointOfSale, 'color') || stc(el.pointOfSale.key);

                        const pointOfSaleDatasetIndex = this.datacollection.datasets.findIndex(dataset => {
                            return dataset.key === el.pointOfSale.key;
                        });
                        if (pointOfSaleDatasetIndex >= 0) {
                            if (this.datacollection.datasets[pointOfSaleDatasetIndex].data[i] === undefined) {
                                this.datacollection.datasets[pointOfSaleDatasetIndex].data[i] = 0;
                            }
                            this.datacollection.datasets[pointOfSaleDatasetIndex].data[i] += el.gross;
                        } else {
                            const pointOfSaleDataset = {
                                key: el.pointOfSale.key,
                                hidden: this.disabledDatasets.indexOf(el.pointOfSale.key) >= 0,
                                label: el.pointOfSale.name,
                                // borderColor: hex,
                                backgroundColor: Color(hex).fade(0.3).rgb().string(),
                                fill: false,
                                // borderDash: [10,10],
                                // lineTension: 0.4,
                                pointHitRadius: 30,
                                pointRadius: 3,
                                pointHoverRadius: 7,
                                data: _.cloneDeep(data)
                            };
                            pointOfSaleDataset.data[i] = el.gross;
                            this.datacollection.datasets.push(pointOfSaleDataset);
                        }
                    } else if (el.sold_by && ['web-shop', 'point-of-sale'].indexOf(el.sold_by) < 0) {
                        const marketplaceKey = 'marketplace:' + el.sold_by;
                        const hex = stc(el.sold_by);

                        const marketplaceDatasetIndex = this.datacollection.datasets.findIndex(dataset => {
                            return dataset.key === marketplaceKey;
                        });
                        if (marketplaceDatasetIndex >= 0) {
                            if (this.datacollection.datasets[marketplaceDatasetIndex].data[i] === undefined) {
                                this.datacollection.datasets[marketplaceDatasetIndex].data[i] = 0;
                            }
                            this.datacollection.datasets[marketplaceDatasetIndex].data[i] += el.gross;
                        } else {
                            const marketplaceDataset = {
                                key: marketplaceKey,
                                hidden: this.disabledDatasets.indexOf(marketplaceKey) >= 0,
                                label: el.sold_by,
                                backgroundColor: Color(hex).fade(0.3).rgb().string(),
                                fill: false,
                                pointHitRadius: 30,
                                pointRadius: 3,
                                pointHoverRadius: 7,
                                data: _.cloneDeep(data)
                            };
                            marketplaceDataset.data[i] = el.gross;
                            this.datacollection.datasets.push(marketplaceDataset);
                        }
                    } else {
                        this.datacollection.datasets[0].data[i] += el.gross;
                    }

                    // // Hauptdatensätze aktualisieren
                    // if (this.datacollection.datasets[0].data[i] === null) {
                    //     this.datacollection.datasets[0].data[i] = 0;
                    // }
                    // this.datacollection.datasets[0].data[i] += el.visitors;

                    // if (this.datacollection.datasets[1].data[i] === null) {
                    //     this.datacollection.datasets[1].data[i] = 0;
                    // }
                    // this.datacollection.datasets[1].data[i] += el.views;
                })
            }

            // // fix gaps
            // this.datacollection.datasets.forEach((dataset, datasetIndex) => {
            //     dataset.data.forEach((el, index) => {
            //         if (!el && (chartIsInThePast || index <= now)) {
            //             this.datacollection.datasets[datasetIndex].data[index] = 0
            //         }
            //     })
            // })
        },
        toggleStacked(stacked) {
            this.stacked = stacked

            this.$set(this.chartOptions.scales.y, 'stacked', stacked)
            this.$set(this.chartOptions.scales.x, 'stacked', stacked)
            this.$set(this.chartOptions.plugins.tooltip, 'mode', stacked ? 'index' : 'nearest')

            this.$nextTick(() => {
                this.generate();
                if (this.$refs.chart) {
                    this.$refs.chart.renderChart(this.datacollection, this.chartOptions)
                }
            })
        }
    },
}
</script>

<style lang="sass" scoped>
/* .filters::v-deep */
/*     .el-form-item__content */
/*         max-width: 200px */
.shop-filters
    display: flex
    gap: 1rem
    margin-bottom: 0.5rem
    flex-wrap: wrap
    &::v-deep .el-form-item
        margin-bottom: 0
        &:last-child
            flex: 1
            .el-select
                width: 100%

form.el-form.filters.el-form--inline
    display: flex
    justify-content: space-between
    flex-wrap: wrap
.title
    margin: 0
    margin-bottom: 1rem
    // text-align: center
    font-weight: 300

.chart-container
    display: flex
    flex-direction: column
    .chart
        flex-grow: 1
    &:hover
        .prev, .next
            opacity: 1
            transform: scale(1)

.force-update-icon
    font-size: 0.8em

// .status-logs-container
//     margin-top: 0
//     margin-bottom: 1rem

.legend
    display: flex
    flex-direction: column
    align-items: flex-end
    .row
        display: flex
        justify-content: space-between
        align-items: center
        width: 100%
    hr
        width: 100%
        background: none
        border: 1px solid $--border-color-base
        border-bottom: none
    ul
        display: flex
        flex-wrap: wrap
        justify-content: flex-end
        li.divider
            width: 1px
            background: $--border-color-base
            margin: 4px 6px
        li .el-button
            position: relative
            padding-right: 10px
            padding-left: 30px
            border:  none
            background: none !important
            span.color
                display: inline-block
                position: absolute
                width: 16px
                height: 16px
                left: 5px
                top: 4px
                border-radius: 50%
                // border: 2px solid
                outline: 1px solid #ffffff
                box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.14), 0 1px 7px 0 rgba(0, 0, 0, 0.12), 0 3px 1px -1px rgba(0, 0, 0, 0.2)
            span.label.strike-through
                text-decoration: line-through
                text-decoration-thickness: 2px
.error
    padding: 20px
    flex-grow: 1
    ::v-deep .title
        margin-top: 0
.title
    position: relative
span.spinner
    position: absolute
    display: inline-block
    width: 24px
    height: 24px
    right: 0
    top: 0
.job
    .summary
        display: flex
        justify-content: space-between
</style>
